/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { endOfMonth, format, startOfMonth, subMonths } from "date-fns";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { downloadCSV } from "@/app/components";
import { createTradingSummaryDataForCSV } from "@/app/components/csv-components/trading-summary";
import notifications from "@/app/container/notification-handler";
import { useCurrencySymbol } from "@/app/hooks/use-currency-symbol";
import { formatDate } from "@/app/lib/format-date";
import { useTradeReportQuery } from "@/app/types/generated/graphql";

import { TradingSummaryViewProps } from "./trading-summary.types";

/** Previous Month start and end date by default */
const defaultStartDate = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
const defaultEndDate = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");

export const useTradingSummary = (): TradingSummaryViewProps => {
  const { t } = useTranslation();

  const { currencySymbol, loading: currencySymbolLoading } = useCurrencySymbol();

  const [{ startDate, endDate }, setDates] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });

  const { data, loading } = useTradeReportQuery({
    variables: {
      where: {
        startDate,
        endDate,
      },
    },
    onError: () => {
      notifications.error({
        description: t("Something went wrong when retrieving the trading summary data."),
      });
    },
  });

  const generateSummary = useCallback(async (startDate: string, endDate: string) => {
    setDates({
      startDate,
      endDate,
    });
  }, []);

  const downloadReport = useCallback(() => {
    const tradingSummary = data?.tradeReport;
    try {
      // convert the data to CSV and download
      if (tradingSummary) {
        downloadCSV(
          `Trading-Summary (${formatDate(tradingSummary.startDate, { noTZ: true })}_to_${formatDate(
            tradingSummary.endDate,
            {
              noTZ: true,
            },
          )}).csv`,
          createTradingSummaryDataForCSV(tradingSummary, currencySymbol),
        );
      } else {
        notifications.error({
          message: t("Error!"),
          description: t("Something went wrong when retrieving the trading summary data."),
        });
      }
    } catch (error) {
      notifications.error({
        message: t("Error!"),
        description: t("Something went wrong when downloading the report."),
      });
    }
  }, [t, data?.tradeReport, currencySymbol]);

  return {
    generateSummary,
    currencySymbol,
    tradingSummary: data?.tradeReport,
    downloadReport,
    loading: loading || currencySymbolLoading,
    startDate,
    endDate,
  };
};
